import { motion } from "framer-motion";
import { DollarSign, Users, Laptop, FileCheck, Home, Landmark, Check } from "lucide-react";

const realEstateInvestments = [
  { label: "Homebuying Customers Provided", icon: Users },
  { label: "Showing Assistants", icon: Home },
  { label: "CRM & Technology Platform", icon: Laptop },
  { label: "MLS & Licensing Fees", icon: FileCheck },
];

const mortgageInvestments = [
  { label: "Pre-Approved Leads", icon: Users },
  { label: "CRM & Technology Platform", icon: Laptop },
  { label: "Licensing Fees", icon: FileCheck },
];

const InvestmentSection = () => {
  return (
    <section className="py-24 md:py-32 relative bg-slate-50" data-testid="investment-section">
      <div className="container-main relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-widest uppercase text-green-600 mb-4 block">
            Our Investment in You
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900">
            We Cover the{" "}
            <span className="gradient-text">Major Expenses</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            CityWorth removes financial barriers so you can focus on building your career,
            not worrying about startup costs.
          </p>
        </motion.div>

        {/* Investment cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Real Estate Investment */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-red-300 transition-colors shadow-sm"
            data-testid="investment-real-estate"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <Home className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Real Estate Side</h3>
                <p className="text-slate-500">What we provide for agents</p>
              </div>
            </div>

            <div className="space-y-4">
              {realEstateInvestments.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-slate-700 font-medium">{item.label}</span>
                  <Check className="w-5 h-5 text-green-500 ml-auto" />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Mortgage Investment */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-blue-300 transition-colors shadow-sm"
            data-testid="investment-mortgage"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Landmark className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Mortgage Side</h3>
                <p className="text-slate-500">What we provide for bankers</p>
              </div>
            </div>

            <div className="space-y-4">
              {mortgageInvestments.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="text-slate-700 font-medium">{item.label}</span>
                  <Check className="w-5 h-5 text-green-500 ml-auto" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 border border-slate-200 shadow-sm">
            <DollarSign className="w-5 h-5 text-green-500" />
            <span className="text-slate-700">
              Zero startup costs. Zero financial barriers. <span className="font-semibold text-slate-900">100% focus on growth.</span>
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestmentSection;
